import { FC } from 'react';
import classNames from 'classnames';
import Link from 'next/link';
import { PrismicRichText } from '@prismicio/react';
import { htmlSerializerWithProps } from '@/utils';
import {
  BlogPostsSliceFullImageWithShadowItem,
  BlogPostsSliceFullImageWithShadowPrimary,
} from '@/types';

import style from '../blog.module.scss';
import {
  BlogFigureImage,
  BlogPublishDate,
  BlogStatsContainer,
} from '../Blog.components';
import { ColorField } from '@prismicio/client';

const FullImageVariation: FC<IProps> = ({ primary, items }) => {
  const DEFAULT_COLORS: Record<
    'titleColor' | 'descriptionColor' | 'iconsColor' | 'countsColor',
    ColorField
  > = {
    titleColor: '#FFFFFF',
    descriptionColor: '#CCD5E3',
    iconsColor: '#FFFFFF',
    countsColor: '#CCD5E3',
  };

  const { title_color, description_color, icons_color, counts_color } = primary;

  const getColor = (
    color: ColorField,
    defaultColor: ColorField,
    mode: 'Default' | 'Text Top' | 'Text Bottom',
  ) => {
    if (mode === 'Default') {
      return color;
    }

    return color ?? defaultColor;
  };

  return (
    <div
      className={classNames(
        style.blogs__articles,
        style.blogs__articles_column_heading,
      )}
    >
      {items?.map((item, index) => {
        const {
          title,
          description,
          date,
          link,
          image,
          total_views,
          total_comments,
          mode,
        } = item;
        const isOverlay = mode !== 'Default';

        return (
          <article key={`fullImage-${index}`}>
            <Link
              className={classNames(style.article, style.article_heading, {
                [style.article_fullWidth]: isOverlay,
              })}
              href={link || ''}
            >
              <blockquote
                className={classNames({
                  [style.blockquote_absolute]: isOverlay,
                  [style.blockquote_absolute__top]: mode === 'Text Top',
                })}
              >
                <BlogPublishDate className={style.time} publishAt={date} />
                <PrismicRichText
                  components={htmlSerializerWithProps({
                    color: getColor(
                      title_color,
                      DEFAULT_COLORS.titleColor,
                      mode,
                    ),
                  })}
                  field={title}
                />
                <PrismicRichText
                  components={htmlSerializerWithProps({
                    color: getColor(
                      description_color,
                      DEFAULT_COLORS.descriptionColor,
                      mode,
                    ),
                  })}
                  field={description}
                />
                <BlogStatsContainer
                  comments={total_comments!}
                  countColor={
                    getColor(counts_color, DEFAULT_COLORS.countsColor, mode)!
                  }
                  iconColor={
                    getColor(icons_color, DEFAULT_COLORS.iconsColor, mode)!
                  }
                  reviews={total_views!}
                />
              </blockquote>
              <BlogFigureImage
                className={classNames(
                  style.article__figure_fullWidthVariation,
                  {
                    [style['article__figure_fullWidthVariation--absolute']]:
                      isOverlay,
                  },
                )}
                image={image}
              />
              {isOverlay && (
                <div
                  className={classNames(style.overlap, {
                    [style['overlap--inverse']]: mode === 'Text Top',
                  })}
                />
              )}
            </Link>
          </article>
        );
      })}
    </div>
  );
};

interface IProps {
  primary: BlogPostsSliceFullImageWithShadowPrimary;
  items: BlogPostsSliceFullImageWithShadowItem[];
}

export default FullImageVariation;
